import { useState } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Button,
  Text,
  VStack,
  HStack,
  Box,
  RadioGroup,
  Radio,
  Input,
  Alert,
  AlertIcon,
  AlertDescription,
} from '@chakra-ui/react';
import { Flag } from 'lucide-react';
import { useReportMenuItem } from '@/api/mutations/useSocialMutations';
import type { MenuItemReportType } from '@/types/social';

interface ReportMenuItemModalProps {
  /** Whether the modal is open */
  isOpen: boolean;
  /** Callback to close the modal */
  onClose: () => void;
  /** Database PK of the menu item being reported */
  menuItemId: number;
  /** Restaurant this menu item belongs to */
  restaurantId: number;
  /** Restaurant display name — shown for user context, not sent in request */
  restaurantName: string;
}

/** Report type options: display label → API slug */
const REPORT_OPTIONS: Array<{ label: string; value: MenuItemReportType }> = [
  { label: 'Item does not Exist', value: 'does_not_exist' },
  { label: 'Wrong Spelling', value: 'wrong_spelling' },
  { label: 'Discontinued Item', value: 'discontinued' },
  { label: 'Duplicate', value: 'duplicate' },
  { label: 'Other', value: 'other' },
];

/**
 * Modal for reporting an inaccurate menu item.
 * Submits to POST /menu-item-reports/ via useReportMenuItem.
 */
export function ReportMenuItemModal({
  isOpen,
  onClose,
  menuItemId,
  restaurantId,
  restaurantName,
}: ReportMenuItemModalProps) {
  const [selectedType, setSelectedType] = useState<MenuItemReportType | ''>('');
  const [otherText, setOtherText] = useState('');
  const [isError, setIsError] = useState(false);

  const { mutateAsync: reportMenuItem, isPending } = useReportMenuItem();

  const handleClose = () => {
    // Reset local state on close so the form is fresh if reopened
    setSelectedType('');
    setOtherText('');
    setIsError(false);
    onClose();
  };

  const handleSubmit = async () => {
    if (!selectedType) return;

    setIsError(false);
    try {
      await reportMenuItem({
        restaurantId,
        menuItemId,
        reportType: selectedType,
        otherText: selectedType === 'other' && otherText.trim() ? otherText.trim() : undefined,
      });
      // Only close on success — modal stays open on failure so the user can retry
      handleClose();
    } catch {
      // Surface inline error; re-enable the submit button via finally-free path
      setIsError(true);
    }
  };

  const canSubmit =
    !!selectedType && (selectedType !== 'other' || otherText.trim().length > 0);

  return (
    <Modal isOpen={isOpen} onClose={handleClose} isCentered>
      <ModalOverlay bg="blackAlpha.700" backdropFilter="blur(8px)" />
      <ModalContent bg="var(--card-bg)" borderRadius="var(--r-card)" mx={4}>
        <ModalHeader>
          <HStack spacing={3}>
            <Box bg="orange.100" borderRadius="var(--r-inner)" p={2}>
              <Flag size={20} color="var(--chakra-colors-orange-500)" />
            </Box>
            <VStack align="start" spacing={0}>
              <Text fontSize="lg" fontWeight="bold" color="var(--card-text)">
                Report Menu Item
              </Text>
              {restaurantName && (
                <Text fontSize="xs" color="var(--card-text-3)" fontWeight="normal">
                  {restaurantName}
                </Text>
              )}
            </VStack>
          </HStack>
        </ModalHeader>
        <ModalCloseButton />

        <ModalBody pb={2}>
          <VStack align="stretch" spacing={4}>
            <Text color="var(--card-text-2)" fontSize="sm">
              What's wrong with this menu item?
            </Text>

            <RadioGroup
              value={selectedType}
              onChange={(value) => setSelectedType(value as MenuItemReportType)}
            >
              <VStack align="start" spacing={3}>
                {REPORT_OPTIONS.map(({ label, value }) => (
                  <Radio key={value} value={value} colorScheme="orange">
                    <Text fontSize="sm" color="var(--card-text-2)">
                      {label}
                    </Text>
                  </Radio>
                ))}
              </VStack>
            </RadioGroup>

            {/* Inline submission error — stays visible so the user can retry */}
            {isError && (
              <Alert status="error" borderRadius="var(--r-inner)" role="alert">
                <AlertIcon />
                <AlertDescription fontSize="sm">
                  Failed to submit report. Please try again.
                </AlertDescription>
              </Alert>
            )}

            {/* Additional details field shown only when "Other" is selected */}
            {selectedType === 'other' && (
              <Input
                placeholder="Please describe the issue..."
                value={otherText}
                onChange={(e) => setOtherText(e.target.value)}
                size="sm"
                fontSize="md"
                borderColor="var(--card-divider)"
                _focus={{ borderColor: 'orange.400', boxShadow: '0 0 0 1px var(--chakra-colors-orange-400)' }}
                aria-label="Additional details for other report type"
              />
            )}
          </VStack>
        </ModalBody>

        <ModalFooter borderTop="1px solid" borderColor="var(--card-divider)" pt={4}>
          <HStack spacing={3} w="100%">
            <Button variant="ghost" flex={1} onClick={handleClose} isDisabled={isPending}>
              Cancel
            </Button>
            <Button
              bg="orange.500"
              color="white"
              _hover={{ bg: 'orange.600' }}
              flex={1}
              onClick={handleSubmit}
              isDisabled={!canSubmit}
              isLoading={isPending}
              loadingText="Submitting..."
            >
              Submit Report
            </Button>
          </HStack>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}

export default ReportMenuItemModal;
